<template>
  <v-main
    ref="authShell"
    class="pa-0 auth-shell"
    :class="{ 'auth-shell--dark': isDarkTheme }"
  >
    <v-row no-gutters class="fill-height flex-nowrap auth-row-full-height">
      <v-col cols="12" lg="6" class="pa-0 d-none d-lg-block auth-col-branding">
        <HospitalBrandingPanel
          :hospital-config="hospitalConfig"
          current-view="restablecer-contrasena"
        />
      </v-col>

      <v-col cols="12" lg="6" class="auth-col-form d-flex flex-column pa-0">
        <div
          class="auth-mobile-header d-flex d-lg-none flex-column align-center justify-center pa-6 text-white"
        >
          <div v-if="mobileLogoUrl" class="auth-mobile-logo mb-3">
            <img
              v-if="mobileLogoUseNativeImg"
              :src="mobileLogoUrl"
              alt=""
              class="auth-mobile-logo__img"
            />
            <v-img
              v-else
              :src="mobileLogoUrl"
              alt=""
              max-height="96"
              max-width="300"
              contain
            />
          </div>
          <v-avatar v-else size="64" class="auth-mobile-avatar mb-3">
            <span class="text-h5 font-weight-bold">{{ hospitalInitial }}</span>
          </v-avatar>
          <template v-if="!mobileLogoUrl">
            <h1 class="text-subtitle-1 font-weight-semibold text-center mb-1">
              {{ hospitalConfig.name }}
            </h1>
          </template>
        </div>

        <div class="auth-form-body flex-grow-1 d-flex align-center justify-center pa-6 pa-lg-12">
          <RestablecerContrasenaForm
            :hospital-config="hospitalConfig"
            :user-id="resetUserId"
            :token="resetToken"
          />
        </div>

        <div class="auth-footer pa-4 text-center text-caption">
          <p class="mb-0 d-flex align-center justify-center flex-wrap ga-2">
            <span class="macena-footer-lead">Un producto de</span>
            <a
              href="https://www.macena.com.ar/"
              target="_blank"
              rel="noopener noreferrer"
              class="macena-footer-link d-inline-flex align-center"
            >
              <img :src="macenaLogoSrc" alt="Macena" />
            </a>
          </p>
        </div>
      </v-col>
    </v-row>
  </v-main>
</template>

<script>
import { mapState } from 'vuex'
import HospitalBrandingPanel from '../../../components/auth/HospitalBrandingPanel.vue'
import RestablecerContrasenaForm from '../../../components/auth/RestablecerContrasenaForm.vue'
import macenaLogo from '../../../assets/img/macena_gray.svg'
import { derivePortalThemeColors } from '../../../utils/helpers/portalColorTheme'

export default {
  name: 'RestablecerContrasenaView',
  components: {
    HospitalBrandingPanel,
    RestablecerContrasenaForm,
  },
  data() {
    return {
      macenaLogoSrc: macenaLogo,
    }
  },
  computed: {
    ...mapState('portalConfig', ['hospitalConfig']),
    hospitalInitial() {
      return (this.hospitalConfig.name || '?').charAt(0).toUpperCase()
    },
    isDarkTheme() {
      const themeName = this.$vuetify?.theme?.global?.name
      const value = themeName && typeof themeName === 'object'
        ? themeName.value
        : themeName
      return value === 'dark'
    },
    portalThemeColors() {
      return derivePortalThemeColors(
        this.hospitalConfig.primaryColor || '#00838F',
        this.hospitalConfig.secondaryColor || '#E0F2F1',
        this.isDarkTheme,
      )
    },
    resetUserId() {
      const q = this.$route.query || {}
      return String(q.userId ?? q.userid ?? '').trim()
    },
    resetToken() {
      const q = this.$route.query || {}
      const raw = q.token ?? q.emailConfirmationToken ?? q.code
      return raw != null && raw !== '' ? String(raw) : ''
    },
    mobileLogoUrl() {
      const url = this.hospitalConfig.logoUrl
      if (url === null || url === undefined || String(url).trim() === '') return null
      const s = String(url).trim()
      if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:') || s.startsWith('blob:')) {
        return s
      }
      const base = process.env.BASE_URL || '/'
      const path = s.startsWith('/') ? s.slice(1) : s
      const baseNorm = base.endsWith('/') ? base : `${base}/`
      return `${baseNorm}${path}`
    },
    mobileLogoUseNativeImg() {
      const u = this.mobileLogoUrl
      return typeof u === 'string' && (u.startsWith('data:') || u.startsWith('blob:'))
    },
  },
  watch: {
    hospitalConfig: {
      handler() {
        this.applyPortalPrimaryVar()
      },
      deep: true,
    },
  },
  mounted() {
    document.title = `${this.hospitalConfig.name} · Restablecer contraseña`
    this.applyPortalPrimaryVar()
  },
  methods: {
    applyPortalPrimaryVar() {
      const el = this.$refs.authShell?.$el
      if (el?.style) {
        const colors = this.portalThemeColors
        el.style.setProperty('--portal-primary', colors.primary)
        el.style.setProperty('--portal-secondary', colors.secondary)
        el.style.setProperty('--portal-surface', colors.surface)
        el.style.setProperty('--portal-background', colors.background)
      }
    },
  },
}
</script>

<style scoped>
.auth-shell {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  --portal-primary: #00838f;
  --portal-secondary: #e0f2f1;
  --portal-surface: #ffffff;
  --portal-background: #f7f9fb;
}
.auth-row-full-height {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
}
.auth-col-branding {
  min-height: 0;
  height: 100%;
}
.auth-col-form {
  min-height: 0;
  max-height: 100%;
}
.auth-shell--dark .auth-col-form,
.auth-shell--dark .auth-form-body,
.auth-shell--dark .auth-footer {
  background: rgb(var(--v-theme-background));
}
.auth-form-body {
  min-height: 0;
  overflow-y: auto;
}
.auth-mobile-header {
  background-color: var(--portal-primary);
}
.auth-mobile-logo {
  max-width: min(320px, 92vw);
  padding: 0;
  border-radius: 0;
  background: transparent;
}
.auth-mobile-logo :deep(img) {
  object-fit: contain;
}
.auth-mobile-logo__img {
  display: block;
  max-height: 96px;
  max-width: min(300px, 92vw);
  width: auto;
  height: auto;
  object-fit: contain;
}
.auth-mobile-avatar {
  background: rgba(255, 255, 255, 0.2);
}
.macena-footer-lead {
  color: #707070;
  font-size: 14px;
}
.macena-footer-link {
  line-height: 0;
}
.macena-footer-link img {
  display: block;
  height: 13px;
  width: auto;
}

@media (max-width: 1279px) {
  .auth-shell {
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
  }

  .auth-row-full-height {
    height: auto;
    max-height: none;
    min-height: 100vh;
    min-height: 100dvh;
  }

  .auth-col-form {
    max-height: none;
    min-height: 100vh;
    min-height: 100dvh;
  }

  .auth-form-body {
    align-items: center !important;
    overflow-y: visible;
  }
}
</style>
